import { useState, useEffect, useCallback, useMemo } from "react";
import dashboardService from "../../../api/dashboardService";
import eventsService from "../../../api/eventsService";
import fieldReportService from "../../../api/fieldReportService";
import inventoryService from "../../../api/inventoryService";

function extractLowStock(records = []) {
  return records
    .filter((r) => {
      const min = r.minStock || 0;
      const qty = r.quantity || 0;
      const item = r.itemId;
      if (!item || item.status === "inactive") return false;
      return min > 0 && qty < min;
    })
    .map((r) => ({
      id: `${r.itemId?._id}-${r.locationId?._id}`,
      name: r.itemId?.name || "Sin nombre",
      sku: r.itemId?.sku || "",
      quantity: r.quantity || 0,
      minStock: r.minStock || 0,
      deficit: (r.minStock || 0) - (r.quantity || 0),
      location: r.locationId?.name || "—",
      unit: r.itemId?.unit || "u",
    }))
    .sort((a, b) => b.deficit - a.deficit)
    .slice(0, 10);
}

function computeMomChange(monthlyData = []) {
  if (monthlyData.length < 2) return null;
  const current = monthlyData[monthlyData.length - 1]?.count ?? 0;
  const previous = monthlyData[monthlyData.length - 2]?.count ?? 0;
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

export function useDashboardData() {
  const [state, setState] = useState({
    kpis: null,
    servicesByMonth: [],
    servicesByType: [],
    eventStats: null,
    fieldReportStats: null,
    lowStock: [],
    loading: true,
    errors: {},
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, errors: {} }));

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    const results = await Promise.allSettled([
      dashboardService.getKpis(),
      dashboardService.getServicesByMonth(),
      dashboardService.getServicesByType(),
      eventsService.getStats({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString(),
      }),
      fieldReportService.getStatistics({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString(),
      }),
      inventoryService.getInventory(),
    ]);

    const errors = {};
    const get = (idx, label) => {
      const r = results[idx];
      if (r.status === "fulfilled") return r.value;
      errors[label] = r.reason?.message || "Error";
      return null;
    };

    const kpisRes = get(0, "kpis");
    const monthlyRes = get(1, "monthly");
    const typeRes = get(2, "types");
    const eventsRes = get(3, "events");
    const fieldReportRes = get(4, "fieldReports");
    const inventoryRes = get(5, "inventory");

    const inventoryRecords = inventoryRes?.data?.data ?? inventoryRes?.data ?? [];

    setState({
      kpis: kpisRes?.data ?? kpisRes ?? null,
      servicesByMonth: monthlyRes?.data ?? monthlyRes ?? [],
      servicesByType: typeRes?.data ?? typeRes ?? [],
      eventStats: eventsRes?.data?.data ?? eventsRes?.data ?? null,
      fieldReportStats: fieldReportRes ?? null,
      lowStock: extractLowStock(Array.isArray(inventoryRecords) ? inventoryRecords : []),
      loading: false,
      errors,
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const derived = useMemo(() => {
    const k = state.kpis || {};
    const pipeline = {
      pending: k.servicesPending ?? 0,
      scheduled: k.servicesScheduled ?? 0,
      inProgress: k.servicesInProgress ?? 0,
      completed: k.servicesActive ?? 0,
    };
    const pipelineTotal =
      pipeline.pending + pipeline.scheduled + pipeline.inProgress + pipeline.completed;

    const completionRate =
      pipelineTotal > 0
        ? Math.round((pipeline.completed / pipelineTotal) * 100)
        : 0;

    return {
      pipeline,
      pipelineTotal,
      completionRate,
      momChange: computeMomChange(state.servicesByMonth),
    };
  }, [state.kpis, state.servicesByMonth]);

  return { ...state, ...derived };
}
